import { useState, useEffect, useMemo, useContext } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { context } from "@/Layout";
import ButtonIcon from "@/assets/img/menu_icon.svg";
import useUtils from "@/hooks/useUtils";
import { DESKTOP } from "@/utils/appConstants";
import { NavParentRevampPropTypes } from "./propTypes";
import NavItemRevamp from "../NavItemRevamp/NavItemRevamp";

const NavParentRevamp = ({ navItem }) => {
  const { convertToRelative, device } = useUtils();
  const [opened, setOpened] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const desktop = device === DESKTOP;

  const { lenisInstance, toggleIsOpened: setToggleIsOpened } = useContext(context);

  const menuLink = useMemo(() => convertToRelative(navItem.el.url), []);
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;
  const isCurrentMenuLink = currentUrl === menuLink;

  const isSectionLink = useMemo(() => menuLink.length > 1 && menuLink.startsWith("#"), [menuLink]);

  const scrollToSection = (section) => {
    if (!desktop) {
      setToggleIsOpened("false");
    }

    const isPreview =
      window.__NS_PREVIEW__ && new URLSearchParams(location.search).get("preview") === "true";

    if (isPreview) {
      navigate({ pathname: "/", hash: section });
      return;
    }

    const offset = -200;

    if (lenisInstance) {
      setTimeout(() => {
        lenisInstance.scrollTo(section, { offset });
      }, 100);
    } else {
      if (!section) return;

      const id = section.startsWith("#") ? section.slice(1) : section;
      const el = document.getElementById(id);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY + offset;

      window.scrollTo({
        top,
        behavior: "auto",
      });
    }
  };

  const toggleIsOpened = () => {
    if (!desktop) {
      setOpened(!opened);
    }
  };

  const toggleIsOpenedHover = () => {
    if (desktop) {
      setOpened(true);
    }
  };

  const toggleIsOpenedHoverOut = () => {
    if (desktop) {
      setOpened(false);
    }
  };

  useEffect(() => {
    if (opened) {
      toggleIsOpened();
    }
  }, [location]);

  return (
    <div
      className="menu-item-revamp relative flex flex-col mb-4 lg:mb-0"
      onClick={() => toggleIsOpened()}
      onMouseEnter={() => toggleIsOpenedHover()}
      onMouseLeave={() => toggleIsOpenedHoverOut()}
    >
      {isSectionLink ? (
        <button
          className={clsx(
            "menu-link-revamp nav-link main-menu text-watermelon text-center p-[26.5px] w-full relative lg:px-0 hover:text-white transition-colors",
            {
              "lg:pr-4": navItem.children.length > 0,
            }
          )}
          onClick={() => scrollToSection(menuLink)}
        >
          <span dangerouslySetInnerHTML={{ __html: navItem.el.content }}></span>
        </button>
      ) : (
        <NavLink
          className={({ isPending, isTransitioning }) =>
            clsx(
              "menu-link-revamp nav-link main-menu text-center p-[26.5px] w-full relative lg:px-0 transition-colors hover:text-watermelon lg:text-watermelon lg:hover:text-white",
              {
                "text-watermelon font-medium pointer-events-none lg:text-white":
                  isCurrentMenuLink || isPending || isTransitioning,
                "text-white": !isCurrentMenuLink,
                "lg:pr-4": navItem.children.length > 0,
              }
            )
          }
          to={menuLink}
        >
          <span dangerouslySetInnerHTML={{ __html: navItem.el.content }}></span>
        </NavLink>
      )}
      {navItem.children.length > 0 && (
        <img
          src={ButtonIcon}
          alt="Arrow icon"
          className={`absolute right-0 lg:right-0 top-5 transition-transform ${
            opened ? "rotate-180" : ""
          }`}
        />
      )}
      {navItem.children.length > 0 && (
        <NavItemRevamp subNavItem={navItem.children} isOpened={opened} />
      )}
    </div>
  );
};

NavParentRevamp.propTypes = NavParentRevampPropTypes;

export default NavParentRevamp;
